import { useEffect, useMemo, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { TeamPlacement } from "@/components/people/TeamPlacement";
import { SDTD, UNIT } from "@/components/people/roles";

const EMAIL = /[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const blank = { team: UNIT, team_role: "", home: "", units: [], access_role: "" };

const Outcome = ({ result }) => (
  <div className="space-y-2 border border-border bg-muted/40 p-3 text-xs" data-testid="bulk-invite-result">
    <div className="flex flex-wrap gap-4 font-bold">
      <span data-testid="bulk-invited-count">Invited · {result.invited.length}</span>
      <span data-testid="bulk-skipped-count">Skipped · {result.skipped.length}</span>
      <span data-testid="bulk-failed-count">Not sent · {result.failed.length}</span>
    </div>
    {[...result.skipped, ...result.failed].map((r) => (
      <div key={r.email} className="font-data text-muted-foreground">
        {r.email} — {r.why}
      </div>
    ))}
  </div>
);

/** Many e-mail ids, one team, one role, one set of units — invited together. */
export const BulkInviteDialog = ({ open, onOpenChange, data, onDone }) => {
  const [text, setText] = useState("");
  const [place, setPlace] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (open) {
      setText("");
      setPlace(blank);
      setResult(null);
    }
  }, [open]);
  const emails = useMemo(() => [...new Set((text.match(EMAIL) || []).map((e) => e.toLowerCase()))], [text]);
  const ready = emails.length && place.team_role && (![UNIT, SDTD].includes(place.team) || place.home);

  const send = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/people/invite-bulk", {
        emails: text, ...place, home: place.home || null, access_role: place.access_role || null,
      });
      setResult(res);
      toast.success(`${res.invited.length} invited.`);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none" data-testid="bulk-invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black">Invite people to the portal</DialogTitle>
          <DialogDescription>
            Paste the e-mail ids, separated by commas. Pick the team, the role and the units once — every
            one of them is invited the same way and gets a sign-in passcode by mail.
          </DialogDescription>
        </DialogHeader>
        <label className="block">
          <span className="label-caps">E-mail ids</span>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder="a.kumar@sail.in, b.singh@sail.in, c.das@sail.in"
            className="mt-1 rounded-none font-data text-xs"
            data-testid="bulk-invite-emails"
          />
          <span className="mt-1 block text-[11px] text-muted-foreground" data-testid="bulk-invite-read">
            {emails.length} e-mail id(s) read
          </span>
        </label>
        <TeamPlacement
          catalog={data.catalog}
          plants={data.plants}
          teams={Object.keys(data.catalog)}
          guestAccess={data.guest_access}
          value={place}
          onChange={setPlace}
          testPrefix="bulk-invite"
        />
        {result && <Outcome result={result} />}
        <div className="flex justify-end border-t border-border pt-4">
          <Button
            onClick={send}
            disabled={!ready || busy}
            data-testid="bulk-invite-send"
            className="rounded-none font-display text-xs font-bold uppercase tracking-widest"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Invite {emails.length || ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
